import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SLIDE_THEMES = {
  happyHour: {
    bg: 'linear-gradient(135deg, #1a0a00 0%, #dc5024 50%, #f97316 100%)',
    headline: 'Happy Hour',
    subtext: 'Starts at 4 PM',
    accent: '🍻',
    tag: 'Promotion',
  },
  hiring: {
    bg: 'linear-gradient(135deg, #0f1b2f 0%, #1e3a5f 60%, #dc5024 100%)',
    headline: 'Now Hiring',
    subtext: 'Join our team today',
    accent: '💼',
    tag: 'Hiring',
  },
  coffee: {
    bg: 'linear-gradient(160deg, #1c1008 0%, #5c3d2e 40%, #d4a574 100%)',
    headline: 'Fresh Coffee',
    subtext: 'Brewed daily · All day',
    accent: '☕',
    tag: 'Daily Special',
  },
  injera: {
    bg: 'linear-gradient(135deg, #1a0f00 0%, #c2410c 40%, #fbbf24 100%)',
    headline: 'Fresh Injera',
    subtext: 'Available today',
    accent: '🫓',
    tag: 'Special',
  },
  lakers: {
    bg: 'linear-gradient(135deg, #0f1b2f 0%, #552583 50%, #fdb927 100%)',
    headline: 'Lakers Tonight',
    subtext: 'Game starts 7:30 PM',
    accent: '🏀',
    tag: 'Sports',
  },
  birthday: {
    bg: 'linear-gradient(135deg, #1a0533 0%, #7c3aed 50%, #f472b6 100%)',
    headline: 'Happy Birthday',
    subtext: 'Maria!',
    accent: '🎉',
    tag: 'Celebration',
  },
  candy: {
    bg: 'linear-gradient(135deg, #0f1b2f 0%, #dc2626 60%, #fbbf24 100%)',
    headline: '2 for $3',
    subtext: 'All candy bars',
    accent: '🍫',
    tag: 'Sale',
  },
};

export default function ScreenPreview({ theme = 'happyHour', className = '', animate = true }) {
  const slide = SLIDE_THEMES[theme] || SLIDE_THEMES.happyHour;

  return (
    <div className={`relative ${className}`}>
      <div className="absolute -inset-4 bg-invert-orange/20 blur-3xl rounded-full opacity-40" />
      <div className="relative bg-invert-navy rounded-2xl p-3 shadow-2xl shadow-black/40">
        <div className="flex items-center gap-2 px-2 py-1.5 mb-2">
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <div className="w-2 h-2 rounded-full bg-yellow-500" />
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-[10px] text-slate-500 ml-2 font-mono">INVERT Display</span>
        </div>
        <div
          className="relative aspect-video rounded-lg overflow-hidden flex flex-col items-center justify-center text-center p-6"
          style={{ background: slide.bg }}
        >
          {animate && (
            <motion.div
              className="absolute inset-0 opacity-30"
              animate={{
                background: [
                  'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                  'radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                  'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                ],
              }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}
          <span className="text-xs font-semibold uppercase tracking-widest text-white/70 mb-3 relative z-10">
            {slide.tag}
          </span>
          <motion.div
            className="text-5xl mb-3 relative z-10"
            animate={animate ? { scale: [1, 1.1, 1] } : {}}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {slide.accent}
          </motion.div>
          <h3 className="text-3xl lg:text-4xl font-bold text-white mb-2 relative z-10 tracking-tight">
            {slide.headline}
          </h3>
          <p className="text-lg text-white/80 relative z-10">{slide.subtext}</p>
          {theme === 'birthday' && animate && (
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(12)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    background: ['#f472b6', '#fbbf24', '#a78bfa', '#34d399'][i % 4],
                    left: `${10 + (i * 7) % 80}%`,
                    top: `${10 + (i * 11) % 60}%`,
                  }}
                  animate={{ y: [0, -20, 0], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5 + i * 0.1, repeat: Infinity }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { SLIDE_THEMES };
